import { useState } from "react";
import { useListEconomatEcoles } from "@/lib/backoffice-hooks";
import { usePaginationState } from "@/lib/use-paginated-query";
import { useDebouncedValue } from "@/lib/use-debounced-value";
import { PaginationControls } from "@/components/PaginationControls";
import EconomatShell from "@/components/layout/EconomatShell";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Loader2, Search, ChevronRight } from "lucide-react";
import { Link } from "wouter";

function fcfa(n: number) { return new Intl.NumberFormat("fr-FR").format(n) + " FCFA"; }

export default function EconomatEcoles() {
  const { page, size, setPage, setSize, resetToFirstPage } = usePaginationState(20);
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 300);

  const onSearchChange = (v: string) => { setQ(v); resetToFirstPage(); };

  const { data, isLoading } = useListEconomatEcoles({
    page,
    size,
    q: debouncedQ.trim() || undefined,
  });
  const ecoles = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <EconomatShell>
      <div className="space-y-5">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Écoles du diocèse</div>
            <h1 className="text-2xl font-bold text-navy">{total} établissements</h1>
          </div>
          <div className="relative w-72">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => onSearchChange(e.target.value)} placeholder="Rechercher école / commune" className="pl-9" data-testid="input-search-ecoles" />
          </div>
        </header>

        <Card className="p-0 border border-slate-200 shadow-none overflow-hidden">
          {isLoading && <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-navy" /></div>}
          {!isLoading && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200 bg-slate-50">
                  <tr>
                    <th className="text-left px-4 py-2.5">École</th>
                    <th className="text-left">Commune</th>
                    <th className="text-left">Type</th>
                    <th className="text-right">Élèves</th>
                    <th className="text-right">Paiements</th>
                    <th className="text-right pr-2">Encaissé</th>
                    <th className="text-center">Statut</th>
                    <th className="pr-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {ecoles.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-slate-500">Aucune école.</td></tr>}
                  {ecoles.map((e) => (
                    <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`row-ecole-${e.id}`}>
                      <td className="px-4 py-2 font-medium text-navy truncate max-w-[280px]">
                        <Link href={`/economat/ecoles/${e.id}`} className="hover:underline" data-testid={`link-ecole-${e.id}`}>{e.denomination}</Link>
                      </td>
                      <td className="text-slate-600">{e.communeNom ?? "·"}</td>
                      <td className="text-xs text-slate-600">{(e.typeEnseignement ?? "").replace(/_/g, " ")}</td>
                      <td className="text-right font-mono text-slate-700" style={{ fontFamily: "JetBrains Mono, monospace" }}>{e.nbEleves}</td>
                      <td className="text-right font-mono text-slate-700" style={{ fontFamily: "JetBrains Mono, monospace" }}>{e.nbPaiements}</td>
                      <td className="text-right font-mono text-navy pr-2" style={{ fontFamily: "JetBrains Mono, monospace" }}>{fcfa(e.totalEncaisseXof)}</td>
                      <td className="text-center">
                        <span className={`text-xs px-2 py-0.5 rounded ${e.actif ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                          {e.actif ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="text-right pr-4">
                        <Link href={`/economat/ecoles/${e.id}`} className="text-xs text-navy hover:underline inline-flex items-center gap-0.5">
                          Voir <ChevronRight className="w-3 h-3" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <PaginationControls page={page} size={size} total={total} onPageChange={setPage} onSizeChange={setSize} />
            </div>
          )}
        </Card>
      </div>
    </EconomatShell>
  );
}
